import {
	createStyles,
	Flex,
	Group,
	Modal,
	Stack,
	TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { SettingsTitle } from '@repo/common/components/SettingsTypography';
import { DEFAULT_TEMPLATE_ICONS } from '@repo/common/constants/defaultIcons';
import { EntityType } from '@repo/common/enums/entityType';
import { Button, Text, Title } from '@repo/foundations';
import { useFormik } from 'formik';
import { isEqual } from 'lodash-es';
import { useMemo, useRef } from 'react';
import * as Yup from 'yup';
import { queryClient, useSecodaEntityList } from '../../../api';
import {
	templatesQueryKeyFactory,
	useCreateTemplate,
	useTemplate,
	useUpdateTemplate,
} from '../../../api/hooks/templates';
import type { ISecodaEntity } from '../../../api/types';
import { MultiTeamsSelector } from '../../MultiTeamsSelector/MultiTeamsSelector';
import { CustomizeTemplate } from './CustomizeTemplate';

const ERROR_MSG = 'This field is required.';

// Select only changed fields for partial update
function getChangedFields<T extends Record<string, unknown>>(
	currentValues: T,
	initialValues: T
): Partial<T> {
	const changedFields: Partial<T> = {};

	for (const key of Object.keys(currentValues) as (keyof T)[]) {
		const currentValue = currentValues[key];
		const initialValue = initialValues[key];

		if (!isEqual(currentValue, initialValue)) {
			changedFields[key] = currentValue;
		}
	}

	return changedFields;
}

const useStyles = createStyles((theme) => ({
	frame: {
		borderRadius: theme.other.borderRadius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		background: theme.other.getColor('surface/primary/default'),
		padding: theme.spacing.md,
	},
}));

export type TemplateFormik = {
	name: string;
	usable_by_teams: string[];
	title: string;
	icon: string;
	description: string;
	published: boolean;
	status: string;
	owners: string[];
	owners_groups: string[];
	assigned_to: string;
	assigned_to_group: string;
	teams: string[];
	collections: string[];
	tags: string[];
	definition: string;
	synonyms: string[];
	related_resources: ISecodaEntity[];
};

interface ManageTemplateProps {
	navigate: (path: string) => void;
	type: EntityType;
	templateId: string;
	setTemplateId: (templateId: string) => void;
}

export function ManageTemplatePage({
	navigate,
	type,
	templateId,
	setTemplateId,
}: ManageTemplateProps) {
	const { classes } = useStyles();
	const [opened, { open, close }] = useDisclosure(false);
	const editing = templateId !== '';

	const { data: template } = useTemplate({
		id: templateId!,
		options: {
			enabled: !!templateId && templateId !== '',
		},
	});

	const relatedResourcesConfig = useMemo(
		() => ({
			filters: {
				id__in: template?.related_resources || [],
			},
			options: {
				enabled: !!template?.related_resources?.length,
			},
		}),
		[template?.related_resources]
	);

	const { data: relatedResourcesData } = useSecodaEntityList(
		relatedResourcesConfig
	);

	const { mutateAsync: createTemplate } = useCreateTemplate({
		options: {
			onSuccess: () => {
				setTemplateId('');
				navigate('/settings/templates');
				queryClient.invalidateQueries({
					queryKey: templatesQueryKeyFactory.list(1),
				});
			},
		},
	});

	const { mutateAsync: updateTemplate } = useUpdateTemplate({
		options: {
			onSuccess: () => {
				navigate('/settings/templates');
				setTemplateId('');
				queryClient.invalidateQueries({
					queryKey: templatesQueryKeyFactory.list(1),
				});
			},
		},
	});

	const showTitle = [
		EntityType.glossary,
		EntityType.document,
		EntityType.collection,
		EntityType.question,
	].includes(type);

	const formik = useFormik<TemplateFormik>({
		initialValues: {
			name: template?.name || '',
			usable_by_teams: template?.usable_by_teams || [],
			title: template?.title || '',
			icon:
				template?.icon ||
				`iconName:${DEFAULT_TEMPLATE_ICONS[type] || 'folder'} iconColor:#4a4a4a`,
			description: template?.description || '',
			published: template?.published || false,
			status: template?.status || 'UNANSWERED',
			owners: template?.owners || [],
			owners_groups: template?.owners_groups || [],
			assigned_to: template?.assigned_to || '',
			assigned_to_group: template?.assigned_to_group || '',
			teams: template?.teams || [],
			collections: template?.collections || [],
			tags: template?.tags || [],
			definition: template?.definition || '',
			synonyms: template?.synonyms || [],
			related_resources: relatedResourcesData?.results || [],
		},
		enableReinitialize: true,
		validationSchema: Yup.object({
			name: Yup.string().required(ERROR_MSG),
			usable_by_teams: Yup.array().min(1, ERROR_MSG),
			title: showTitle ? Yup.string().required(ERROR_MSG) : Yup.string(),
			icon: Yup.string().required('Icon is required'),
			description: Yup.string(),
			published: Yup.boolean(),
			status: Yup.string(),
			owners: Yup.array(),
			owners_groups: Yup.array(),
			assigned_to: Yup.string(),
			assigned_to_group: Yup.string(),
			teams: Yup.array(),
			collections: Yup.array(),
			tags: Yup.array(),
			definition: Yup.string(),
			synonyms: Yup.array(),
			related_resources: Yup.array(),
		}),
		onSubmit: (values) => {
			// Process related_resources to extract IDs
			const processValue = (val: TemplateFormik): Record<string, unknown> => ({
				...val,
				related_resources: Array.isArray(val.related_resources)
					? (val.related_resources as ISecodaEntity[]).map(
							(entity) => entity.id
						)
					: [],
			});

			if (editing) {
				// For updates, only send the fields that have changed (partial update)
				const changedFields = getChangedFields(
					processValue(values) as Record<string, unknown>,
					processValue(formik.initialValues) as Record<string, unknown>
				);

				// Only update if there are actual changes
				if (Object.keys(changedFields).length > 0) {
					updateTemplate({
						data: {
							id: templateId!,
							...changedFields,
						},
					});
				} else {
					// No changes
					navigate('/settings/templates');
					return;
				}
			} else {
				createTemplate({
					data: {
						...processValue(values),
						entity_type_template: type,
					},
				});
			}

			queryClient.invalidateQueries({
				queryKey: templatesQueryKeyFactory.list(1),
			});
		},
	});

	const lastDirtyValue = useRef<boolean>(formik.dirty);

	if (formik.dirty !== lastDirtyValue.current) {
		lastDirtyValue.current = formik.dirty;
		sessionStorage.setItem('templateFormDirty', formik.dirty.toString());
	}

	return (
		<Stack spacing="2xl">
			<Flex justify="space-between" align="center" mb="3xs">
				<SettingsTitle>
					{editing ? 'Edit' : 'Create'} {type} template
				</SettingsTitle>
			</Flex>
			<form onSubmit={formik.handleSubmit}>
				<Stack spacing="2xl">
					<Stack>
						<Title size="md">Details</Title>
						<Stack spacing="md" className={classes.frame}>
							<TextInput
								autoCorrect="off"
								autoComplete="off"
								key="name"
								name="name"
								label="Name"
								size="xs"
								value={formik.values.name}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								data-testid="name-input"
								error={formik.touched.name ? formik.errors.name : undefined}
							/>
							<MultiTeamsSelector
								label="Team access"
								value={formik.values.usable_by_teams}
								setValue={(val) => {
									formik.setFieldValue('usable_by_teams', val);
								}}
								data-testid="team-access-selector"
								required
								error={
									formik.touched.usable_by_teams &&
									formik.errors.usable_by_teams
										? String(formik.errors.usable_by_teams)
										: undefined
								}
							/>
						</Stack>
					</Stack>
					<Stack>
						<Title size="md">Customize</Title>
						<Stack spacing="md" className={classes.frame}>
							<CustomizeTemplate formik={formik} type={type} />
						</Stack>
					</Stack>
					<Group position="right" mt="lg">
						<Button
							type="button"
							onClick={() => {
								if (formik.dirty) {
									open();
								} else {
									navigate('/settings/templates');
								}
							}}
						>
							Cancel
						</Button>
						<Button variant="primary" type="submit">
							{editing ? 'Save' : 'Create'}
						</Button>
					</Group>
				</Stack>
			</form>

			<Modal
				opened={opened}
				onClose={close}
				title="Leave page with unsaved changes?"
			>
				<Text>Changes you made may not be saved.</Text>
				<Group position="right" mt={12}>
					<Button onClick={close}>Stay</Button>
					<Button
						variant="primary"
						onClick={() => {
							navigate('/settings/templates');
							close();
						}}
					>
						Leave
					</Button>
				</Group>
			</Modal>
		</Stack>
	);
}
